import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useParams, useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AIResponseLoader from '../../common/AIResponseLoader';
import AIResponseError from '../../common/AIResponseError';
import BreadcrumbCrumb from '../../common/Breadcrumbs/BreadcrumbCrumb';
import PageBreadcrumbs from '../../common/Breadcrumbs/PageBreadcrumbs';
import { TruncatedTitle } from '../../common/TruncatedTitle';
import SummaryHeader from './SummaryHeader';
import SummaryBody from './SummaryBody';
import { getData } from '../../../DataAccessLayer';
import { summaries } from '../../../DataAccessLayer/services';
import { RESOURCE_STATUS, RESOURCE_ERROR_MESSAGES } from '../../common/constants';
import { sanitizeSummaryHtml } from '../../common/helpers';

function SummaryDetails() {
  const { summaryId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const knownSummaryTitle = state?.title;
  const isImpersonating = useSelector((s) => s.impersonation.isImpersonating);
  const courseId = useSelector((s) => s.course.courseId);
  const showBanner = isImpersonating || !!courseId;

  const { summary, setSummary, isLoading, loadError } = useOutletContext();

  const handleCopy = async () => {
    try {
      // The clipboard gets two versions: text/html, which formatted targets
      // (Word, OneNote, Google Docs, email, Canvas) paste with headings, bold,
      // colors and lists, and text/plain for plain-text targets. The HTML is
      // cleaned first, the same as the page shows it, so the copy doesn't rely
      // on the stored HTML being clean either; the plain text is taken from
      // the cleaned HTML.
      const clean = sanitizeSummaryHtml(summary?.content);
      const blob = new Blob([clean], { type: 'text/html' });
      const plainBlob = new Blob(
        [new DOMParser().parseFromString(clean, 'text/html').body.textContent ?? ''],
        { type: 'text/plain' },
      );
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blob, 'text/plain': plainBlob }),
      ]);
      toast.success('Summary notes copied to clipboard.');
    } catch (err) {
      console.log(err);
      toast.error('Failed to copy summary notes. Please try again.');
    }
  };

  return (
    <Box
      className={'mss__summaryDetails' + (showBanner ? ' impersonating' : '')}
      id="summaryDetails"
    >
      <PageBreadcrumbs id="summaryDetails__nav_breadcrumbs" rootTo="../.." isVisible={!loadError}>
        <BreadcrumbCrumb isLoading={isLoading && !knownSummaryTitle}>
          <TruncatedTitle name={summary?.title ?? knownSummaryTitle} variant="inherit" />
        </BreadcrumbCrumb>
      </PageBreadcrumbs>

      {isLoading && (
        <Skeleton
          id="summaryDetails__skeleton_loading"
          variant="rectangular"
          height={240}
          sx={{ mt: '1rem' }}
        />
      )}

      {loadError && (
        <AIResponseError
          message={loadError}
          onGoToDashboard={() => navigate('../..', { relative: 'path' })}
        />
      )}

      {!isLoading && !loadError && summary?.status === RESOURCE_STATUS.IN_PROGRESS && (
        <AIResponseLoader
          poll={() => getData(summaries({ id: summaryId, courseId }))}
          isComplete={(data) => data.status === RESOURCE_STATUS.COMPLETED}
          isErrored={(data) => data.status === RESOURCE_STATUS.ERROR}
          onComplete={(data) => setSummary(data)}
          resource="Summary Notes"
          onGenerateNew={() => navigate('..', { relative: 'path' })}
          onGoToDashboard={() => navigate('../..', { relative: 'path' })}
        />
      )}

      {!isLoading && !loadError && summary?.status === RESOURCE_STATUS.ERROR && (
        <AIResponseError
          message={RESOURCE_ERROR_MESSAGES[summary.errorCode]?.('summary notes')}
          resource="Summary Notes"
          onGenerateNew={() => navigate('..', { relative: 'path' })}
          onGoToDashboard={() => navigate('../..', { relative: 'path' })}
        />
      )}

      {!isLoading && !loadError && summary?.status === RESOURCE_STATUS.COMPLETED && (
        <>
          <SummaryHeader summary={summary} onCopy={handleCopy} />
          <SummaryBody content={summary.content} />
        </>
      )}
    </Box>
  );
}

export default SummaryDetails;
